import OfflinePreparation from '../components/OfflinePreparation.jsx';
import Modal from '../components/Modal.jsx';
import { totalLabel } from '../engine/costs.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth, Loading } from '../App.jsx';
import { BrandBar } from '../components/Brand.jsx';
import CatalogueDialog from '../components/CatalogueDialog.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import { useToast } from '../components/Toast.jsx';
import { blankDoc } from '../engine/constants.js';
import SiteBackup from '../components/SiteBackup.jsx';
import AppRail from '../components/AppRail.jsx';
import SampleSites from '../components/SampleSites.jsx';
import Icon from '../components/Icon.jsx';
import PlanIllustration from '../components/PlanIllustration.jsx';
import { formatArea } from '../engine/units.js';
import TenantDialog from '../components/TenantDialog.jsx';

const summaryDoc = summary => ({ opt: { measurementSystem: summary?.measurementSystem === 'imperial' ? 'imperial' : 'metric' } });

export default function Projects() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [projects, setProjects] = useState(null);
  const [archived, setArchived] = useState(false);
  const [samplesOpen, setSamplesOpen] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');
  const [catalogue, setCatalogue] = useState(null);
  const [loadError, setLoadError] = useState(''), [deviceOnly, setDeviceOnly] = useState('');
  const [newSiteOpen, setNewSiteOpen] = useState(false), [newName, setNewName] = useState('');

  useEffect(() => {
    let live = true;
    api.catalogues.list({ scope: 'personal', limit: 1 }).then(r => { if (live) setCatalogue(r); }).catch(() => {});
    return () => { live = false; };
  }, [catalogueOpen]);

  const visible = useMemo(() => (projects || []).filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase())), [projects, search]);
  const totals = useMemo(() => (projects || []).reduce((t, p) => ({ cams: t.cams + (p.summary?.cams || 0), area: t.area + (p.summary?.areaM2 || 0) }), { cams: 0, area: 0 }), [projects]);
  const areaSystems = useMemo(() => new Set((projects || []).map(p => p.summary?.measurementSystem === 'imperial' ? 'imperial' : 'metric')), [projects]);
  const totalAreaDoc = summaryDoc({ measurementSystem: areaSystems.size === 1 && areaSystems.has('imperial') ? 'imperial' : 'metric' });
  const changeArchive = value => { setSamplesOpen(false); setArchived(value); setSearch(''); };
  const showSamples = () => { setSamplesOpen(true); setSearch(''); };

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await api.projects.list(archived);
      // With the server unavailable, only Sites held on this device can be
      // listed. An empty device list is an error, not an empty account.
      if (r.deviceOnly && !r.projects.length) { setLoadError(r.error); return; }
      setDeviceOnly(r.deviceOnly ? r.error : '');
      setProjects(r.projects);
    } catch (e) {
      setLoadError(e.message);
    }
  }, [archived, toast]);

  useEffect(() => {
    load(); window.addEventListener('avisera-offline-change', load); window.addEventListener('online', load); window.addEventListener('offline', load);
    return () => { window.removeEventListener('avisera-offline-change', load); window.removeEventListener('online', load); window.removeEventListener('offline', load); };
  }, [load]);

  const openNewSite = () => { setNewName(''); setNewSiteOpen(true); };
  const [backupSite, setBackupSite] = useState(null);
  // Worked examples open from Samples; a new site always starts blank.
  async function create(name) {
    setBusy(true);
    try {
      const r = await api.projects.create(name.trim(), blankDoc(), { allowOfflineCreation: true });
      nav(`/p/${r.project.id}`);
    } catch (e) {
      toast(e.message, 'bad');
      setBusy(false);
    }
  }

  async function archive(p, e) {
    e.stopPropagation();
    try {
      await api.projects.update(p.id, { archived: !p.archived });
      toast(p.archived ? `"${p.name}" restored.` : `"${p.name}" archived.`, 'ok');
      load();
    } catch (err) { toast(err.message, 'bad'); }
  }

  async function remove(p, e) {
    e.stopPropagation();
    if (!confirm(`Delete "${p.name}" for good? Every revision, image and share link goes with it.`)) return;
    try {
      await api.projects.remove(p.id);
      toast(`"${p.name}" deleted.`, 'ok');
      load();
    } catch (err) { toast(err.message, 'bad'); }
  }

  return (
    <div className="page dashboard-page">
      <Modal open={newSiteOpen} onClose={() => !busy && setNewSiteOpen(false)} width={480}>
        <form onSubmit={e => { e.preventDefault(); if (newName.trim() && !busy) create(newName); }}>
          <div className="dhead"><h2>New site</h2></div>
          <div className="dbody"><label className="field">Site name<input autoFocus value={newName} maxLength={120} onChange={e => setNewName(e.target.value)} placeholder="For example, Elm Street office" /></label>
            <p>Use the same editor online or offline. Prepared devices keep new Sites safely on this device until they can upload.</p></div>
          <div className="dfoot"><button className="btn" type="button" disabled={busy} onClick={() => setNewSiteOpen(false)}>Cancel</button><button className="btn primary" type="submit" disabled={busy || !newName.trim()}>{busy ? 'Creating…' : 'Create site'}</button></div>
        </form>
      </Modal>
      <CatalogueDialog open={catalogueOpen} onClose={() => setCatalogueOpen(false)} />
      <TenantDialog open={companyOpen} onClose={() => setCompanyOpen(false)} />
      <header className="topbar">
        <BrandBar />
        <span className="breadcrumb-divider">/</span><button className="header-context company-context" onClick={() => setCompanyOpen(true)}>{user?.tenant?.name || 'Company workspace'}</button>
        <div className="spacer" />
        <div className="topbar-actions">
          <button className="btn" onClick={() => setCatalogueOpen(true)}><Icon name="camera" />Camera library</button>
          <button className="btn" onClick={() => setCompanyOpen(true)}><Icon name="building" />Company</button>
          <ThemeToggle />
          <span className="vsep" />
          <span className="account-avatar" title={user?.name} aria-label={user?.name}>{user?.name?.trim().slice(0, 1).toUpperCase() || 'S'}</span>
          <button className="btn ghost sign-out" aria-label="Sign out" onClick={logout}><Icon name="logout" /><span>Sign out</span></button>
        </div>
      </header>

      <div className="dashboard-layout">
      <AppRail samples={samplesOpen} onSamples={showSamples} archived={archived} onSites={() => changeArchive(false)} onArchive={() => changeArchive(true)} onCatalogue={() => setCatalogueOpen(true)} onCreate={openNewSite} busy={busy} />
      <main className="dashboard-frame">
        <nav className="section-nav" aria-label="Site library">
          <button className={!archived && !samplesOpen ? 'active' : ''} aria-current={!archived && !samplesOpen ? 'page' : undefined} onClick={() => changeArchive(false)}>Site overview</button>
          <button className={archived && !samplesOpen ? 'active' : ''} aria-current={archived && !samplesOpen ? 'page' : undefined} onClick={() => changeArchive(true)}>Archived</button>
          <button className={samplesOpen ? 'active' : ''} aria-current={samplesOpen ? 'page' : undefined} onClick={showSamples}>Samples</button>
          <span className="grow" /><span className="workspace-label"><Icon name="shield" size={15} />Private workspace</span>
        </nav>
      <div className="page-body">
        <div className="page-inner">
          {samplesOpen ? <SampleSites /> : <>
          <div className="page-head">
            <div>
              <h1>{archived ? 'Archived sites' : 'Your sites'}</h1>
              <p className="sub">
                {archived
                  ? 'Archived sites keep their full history and can be restored at any time.'
                  : 'Design, review, and deliver your camera coverage plans.'}
              </p>
            </div>
            <div className="grow" />
            {!archived && (
              <>
                <button className="btn" disabled={busy} onClick={showSamples}><Icon name="file" />Explore samples</button>
                <button className="btn primary" disabled={busy} onClick={openNewSite}><Icon name="plus" />New site</button>
              </>
            )}
          </div>

          {!archived && <OfflinePreparation />}

          <div className="overview-grid">
            <div className="overview-card"><div className="overview-icon"><Icon name="sites" /></div><span className="overview-label">{archived ? 'Archived sites' : 'Active sites'}</span><strong>{projects === null ? '—' : projects.length}</strong><span className="overview-caption">{archived ? 'Ready to restore' : 'In your workspace'}</span></div>
            <div className="overview-card"><div className="overview-icon"><Icon name="camera" /></div><span className="overview-label">Planned cameras</span><strong>{projects === null ? '—' : totals.cams}</strong><span className="overview-caption">Across these sites</span></div>
            <div className="overview-card"><div className="overview-icon"><Icon name="layers" /></div><span className="overview-label">Site area</span><strong>{projects === null ? '—' : formatArea(totals.area, totalAreaDoc, 0)}</strong><span className="overview-caption">{areaSystems.size > 1 ? 'Mixed site units · total shown in metric' : 'Saved floor plans'}</span></div>
            <button className="overview-card catalogue-overview" onClick={() => setCatalogueOpen(true)}><div className="overview-icon blue"><Icon name="camera" /></div><span className="overview-label">My library</span><strong>{catalogue?.total ?? '—'} <small>models</small></strong><span className="overview-caption">{catalogue?.total ? `${catalogue.manufacturers.length} manufacturers · used to start new sites` : 'Add preferred cameras for new sites'}<Icon name="arrow" size={15} /></span></button>
          </div>

          <section className="sites-section" aria-label="Saved sites">
            <div className="section-heading"><h2>{archived ? 'Archived plans' : 'Site plans'}<span className="count-badge">{projects?.length ?? '—'}</span></h2><div className="site-list-controls"><label className="search-control"><Icon name="search" size={16} /><input type="search" aria-label="Search sites" placeholder="Search sites" value={search} onChange={e => setSearch(e.target.value)} /></label><div className="seg view-toggle" role="group" aria-label="Site layout"><button type="button" aria-label="List view" aria-pressed={view === 'list'} className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}><Icon name="list" /></button><button type="button" aria-label="Grid view" aria-pressed={view === 'grid'} className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}><Icon name="grid" /></button></div></div></div>

          {!loadError && deviceOnly && <div className="catalogue-notice device-only-notice" role="status"><div><strong>Showing only Sites saved on this device</strong><p>{deviceOnly} Other Sites in your account appear when the server responds.</p></div><button className="btn" onClick={load}>Try again</button></div>}
          {loadError ? <div className="empty-state"><h3>Could not load your sites</h3><p role="alert">{loadError}</p><button className="btn primary" onClick={load}>Try again</button></div> : projects === null ? (
            <Loading what="Fetching your sites" />
          ) : visible.length === 0 ? (
            <div className="empty-state">
              <PlanIllustration />
              <h3>{search ? 'No matching sites' : archived ? 'Nothing archived' : 'No sites yet'}</h3>
              <p>
                {search ? 'Try another site name or clear your search.' : archived
                  ? 'Sites you archive will appear here.'
                  : 'Start with the worked example to see how the pieces fit together, or open a blank sheet and trace your own floor plan.'}
              </p>
              {search ? <button className="btn" onClick={() => setSearch('')}>Clear search</button> : !archived && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button className="btn solid" disabled={busy} onClick={showSamples}>Explore samples<Icon name="arrow" size={15} /></button>
                </div>
              )}
            </div>
          ) : (
            <div className={'project-grid' + (view === 'list' ? ' project-list' : '')}>
              {view === 'list' && <div className="project-list-heading" aria-hidden="true"><span>Site</span><span>Cameras</span><span>Floor area</span><span>Hardware</span><span>Actions</span></div>}
              {visible.map(p => (
                <div key={p.id} className="project-card" role="group" aria-label={p.name}
                  onClick={() => nav(`/p/${p.id}`)}
                  onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); nav(`/p/${p.id}`); } }}>
                  <div className="project-title-group"><div className="project-icon"><Icon name="folder" size={21} /></div><div className="project-heading">
                  <h3><button className="project-open" onClick={e => { e.stopPropagation(); nav(`/p/${p.id}`); }}>{p.name}</button></h3>
                  <div className="meta">
                    <span className="revision-label">{p.pendingCreation ? 'On this device' : `r${p.rev ?? 1}`}</span>
                    {p.pendingUpload ? <span>Waiting to upload</span> : p.availableOffline ? <span>Available offline</span> : null}
                    <span>Updated {new Date(/[zZ]|[+-]\d\d:\d\d$/.test(p.updatedAt) ? p.updatedAt : p.updatedAt + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  </div></div>
                  <div className="stats">
                    <div className="stat">
                      <div className="k">Cameras</div>
                      <div className="v">{p.summary?.cams ?? 0}</div>
                    </div>
                    <div className="stat">
                      <div className="k">Floor</div>
                      <div className="v">{formatArea(p.summary?.areaM2 ?? 0, summaryDoc(p.summary), 0)}</div>
                    </div>
                    <div className="stat">
                      <div className="k">Hardware</div>
                      <div className="v">{totalLabel(p.summary?.cost || 0, p.summary?.unpriced ?? (p.summary?.cams > 0 && !p.summary?.cost ? p.summary.cams : 0), p.summary?.currency || '$')}</div>
                    </div>
                  </div>
                  <div className="row-actions">
                    <button className="btn sm" aria-label={'Back up ' + p.name} onClick={e => { e.stopPropagation(); setBackupSite(p); }}>Back up</button>
                    <button className="btn sm" disabled={p.pendingUpload} title={p.pendingUpload ? "Upload pending changes before archiving this Site" : undefined} onClick={e => archive(p, e)}>
                      {p.archived ? 'Restore' : 'Archive'}
                    </button>
                    <button className="btn sm ghost danger" disabled={p.pendingUpload} title={p.pendingUpload ? "Upload pending changes before deleting this Site" : undefined} onClick={e => remove(p, e)}>Delete</button>
                    <Icon className="open-site-arrow" name="chevron" size={16} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="sites-footer"><span>{visible.length} {visible.length === 1 ? 'site' : 'sites'}{search ? ` matching “${search}”` : ''}</span><span><Icon name="clock" size={14} />Revision history included</span></div>
          </section>
          </>}
          <footer className="dashboard-footer"><span>Avisera Planner <span>·</span> Camera coverage planning</span><button className="linkish" onClick={() => setCatalogueOpen(true)}>Browse camera library<Icon name="arrow" size={14} /></button></footer>
        </div>
      </div>
      </main>
      </div>
      <SiteBackup open={!!backupSite} onClose={() => setBackupSite(null)} id={backupSite?.id} name={backupSite?.name} />
    </div>
  );
}
